import React, { useContext, useState } from "react";
import Modal from "../../../useOss/Modal";
import { Spinner } from "../../../useOss";
import { PopupContentContext } from "../../../context/PopupContentContext";
import deleteOwnAccount from "../../../api/user/delete";
import { sendButtonAnalytics, sendUserSettingsAnalytics } from "../../../utils";
import { UserInfoResponse } from "../../../api/user/info";

const confirmAccountDeleteText = "delete core instances and all data permanently";

export function DeleteOwnAccountModal({
    isModalOpen,
    closeModal,
    onAccountDeleted,
    userRole,
    user
}: {
    isModalOpen: boolean;
    closeModal: () => void;
    onAccountDeleted: () => void;
    userRole: "owner" | "admin" | "member";
    user: UserInfoResponse;
}) {
    const [state, setState] = useState<"IDLE" | "DELETING">("IDLE");
    const [confirmationText, setConfirmationText] = useState("");

    const { showToast } = useContext(PopupContentContext);
    const disableDelete = userRole === "owner" && confirmationText !== confirmAccountDeleteText;

    async function handleDeleteOwnAccount() {
        try {
            setState("DELETING");
            sendButtonAnalytics("button_delete_account", "v5", {
                email: user.email,
                userId: user.userId
            });
            await deleteOwnAccount();
            showToast({
                status: "success",
                icon: `/static/assets/dashboard/check-rounded-green.png`,
                children: <>Account deleted</>
            });
            sendUserSettingsAnalytics("delete_account_success", {
                email: user.email,
                userId: user.userId
            });
            onAccountDeleted();
            closeModal();
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Could not delete your account</>
            });
            sendUserSettingsAnalytics("delete_account_error", {
                email: user.email,
                userId: user.userId,
                statusCode: "response" in e && e.response.status
            });
            setState("IDLE");
        }
    }
    function renderDeleteAccountButton() {
        switch (state) {
            case "IDLE":
                return (
                    <button
                        className="modal-button red-button"
                        disabled={disableDelete}
                        style={{
                            width: "120px",
                            height: "37px"
                        }}
                        onClick={handleDeleteOwnAccount}
                        id="delete-account-button"
                    >
                        Yes, Delete
                    </button>
                );
            case "DELETING":
                return (
                    <button
                        className="modal-button red-button loading"
                        disabled
                        style={{
                            width: "120px",
                            height: "37px"
                        }}
                        id="delete-account-button"
                    >
                        <Spinner isInline />
                    </button>
                );
        }
    }

    function renderConfirmationInput() {
        if (userRole === "owner") {
            return (
                <div id="delete-account-statement">
                    Are you sure you want to delete your account? This action is irreversible and you will lose all your
                    data permanently.
                    <div>
                        <br />
                        Type <span style={{ color: "#ed344e" }}>{confirmAccountDeleteText}</span> to confirm
                        <input
                            type="text"
                            value={confirmationText}
                            onChange={e => {
                                setConfirmationText(e.target.value);
                            }}
                            style={{
                                maxWidth: "100%",
                                marginTop: "6px"
                            }}
                            placeholder={confirmAccountDeleteText}
                            autoFocus
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div id="delete-account-statement">
                    Are you sure you want to delete your account? Deleting your account will remove you from the
                    organization. However, the organization's data will remain intact and will not be deleted.
                </div>
            );
        }
    }
    return (
        <Modal show={isModalOpen} modalClosed={() => closeModal()} className={`${!isModalOpen && "hide"}`}>
            <section className="modal-header-section centered">
                <h4>Delete Account?</h4>

                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => closeModal()}
                >
                    <path
                        d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22Z"
                        fill="#E8E8E8"
                    />
                    <path
                        d="M7.2481 14.89C7.40424 15.0431 7.61474 15.1279 7.83337 15.1258C8.052 15.1238 8.26088 15.035 8.4141 14.879L11.0041 12.239L13.5951 14.879C13.6699 14.9606 13.7604 15.0264 13.8611 15.0723C13.9618 15.1182 14.0708 15.1433 14.1814 15.1463C14.2921 15.1492 14.4022 15.1298 14.5052 15.0893C14.6082 15.0488 14.7021 14.9879 14.7811 14.9104C14.8601 14.8329 14.9227 14.7402 14.9651 14.638C15.0076 14.5357 15.029 14.426 15.0282 14.3153C15.0274 14.2046 15.0043 14.0952 14.9603 13.9936C14.9163 13.8921 14.8523 13.8004 14.7721 13.724L12.1601 11.063L14.7721 8.40103C14.9167 8.24332 14.9943 8.03562 14.9887 7.82175C14.983 7.60787 14.8945 7.40456 14.7418 7.25471C14.5891 7.10486 14.3841 7.0202 14.1702 7.01858C13.9562 7.01696 13.7501 7.09851 13.5951 7.24603L11.0041 9.88603L8.4141 7.24603C8.3393 7.16443 8.24883 7.09872 8.14809 7.05281C8.04736 7.0069 7.93841 6.98173 7.82775 6.9788C7.71709 6.97588 7.60698 6.99526 7.50396 7.03578C7.40094 7.0763 7.30712 7.13714 7.22811 7.21467C7.1491 7.29221 7.0865 7.38485 7.04405 7.48709C7.00159 7.58932 6.98014 7.69907 6.98098 7.80977C6.98182 7.92046 7.00493 8.02986 7.04893 8.13144C7.09293 8.23302 7.15692 8.3247 7.2371 8.40103L9.8491 11.063L7.2371 13.724C7.08405 13.8802 6.99924 14.0907 7.00131 14.3093C7.00337 14.5279 7.09214 14.7368 7.2481 14.89Z"
                        fill="#6E6A65"
                    />
                </svg>
            </section>
            <hr />

            {renderConfirmationInput()}

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <button
                    className="modal-button white-button"
                    style={{
                        marginRight: "24px"
                    }}
                    onClick={() => closeModal()}
                >
                    Cancel
                </button>
                {renderDeleteAccountButton()}
            </div>
        </Modal>
    );
}
